import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Loader2 } from "lucide-react";
import { prefersReducedMotion } from "../context/PaintContext";
import { createScrubAudio } from "../lib/scrubAudio";
import { BASE, V, MEDIA, useVariant, useDeferredSource, videoUrl } from "./hero/media";
import { HeroIntro } from "./hero/HeroIntro";
import { WallEnding } from "./hero/WallEnding";
import { useHeroTilt } from "./hero/useHeroTilt";

// Scroll share after which the film fades away and reveals the site underneath.
export const HERO_FADE_START = 0.92;
// Opening copy: fully visible to 15% of the scroll, gone (with a 20px drift up) by 35%.
const INTRO_HOLD = 0.15;
const INTRO_LEN = 0.2;
// The wall is empty from about 79% of the film; the wordmark is brushed on there.
const MARK_START = 0.79;
const MARK_LEN = 0.05;
const LINE_START = 0.84;
const LINE_LEN = 0.03;
const CALL_START = 0.865;
const CALL_LEN = 0.025;
const EASE_MS = 110;

const clamp = (v) => Math.min(1, Math.max(0, v));
const smooth = (v) => v * v * (3 - 2 * v);

// Optional pour soundtrack, synced to the scroll scrub. Off by default; turning it on is the click browsers need.
const useSound = () => {
  const engine = useRef(null);
  const on = useRef(false);
  const [state, setState] = useState("off");
  if (!engine.current) engine.current = createScrubAudio(`${BASE}/sab-pour-audio.mp3?${V}`);
  useEffect(() => () => engine.current.disable(), []);
  const update = useCallback((time) => on.current && engine.current.update(time), []);
  const toggle = useCallback(async () => {
    if (on.current) {
      on.current = false;
      engine.current.disable();
      setState("off");
      return;
    }
    setState("loading");
    try {
      await engine.current.enable();
      on.current = true;
      setState("on");
    } catch {
      setState("off");
    }
  }, []);
  return { state, update, toggle };
};

const SoundToggle = ({ sound }) => {
  const Icon = sound.state === "loading" ? Loader2 : sound.state === "on" ? Volume2 : VolumeX;
  return (
    <button
      type="button"
      onClick={sound.toggle}
      aria-pressed={sound.state === "on"}
      className="absolute bottom-20 right-4 z-20 inline-flex h-11 items-center gap-2 rounded-full border border-charcoal/20 bg-beige/80 px-4 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal backdrop-blur-md transition-colors hover:border-charcoal hover:bg-beige md:bottom-8 md:right-8"
      data-testid="hero-sound-toggle"
    >
      <Icon className={`h-4 w-4 ${sound.state === "loading" ? "animate-spin" : ""}`} aria-hidden="true" />
      {sound.state === "on" ? "Sound on" : "Sound off"}
    </button>
  );
};

const fadeIn = (el, p, start, len, rise) => {
  if (!el) return 0;
  const v = smooth(clamp((p - start) / len));
  el.style.opacity = String(v);
  el.style.transform = `translateY(${(1 - v) * rise}px)`;
  return v;
};

// Scroll-scrubbed film for every visitor: scrolling down plays it forward, scrolling up rewinds it.
// It only moves when the visitor scrolls; with reduced motion it tracks the scroll directly (no glide or zoom).
const ScrollHero = ({ onReveal }) => {
  const reduce = prefersReducedMotion();
  const media = MEDIA[useVariant()];
  const src = useDeferredSource(videoUrl(media));
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const sceneRef = useRef(null);
  const introRef = useRef(null);
  const markRef = useRef(null);
  const lineRef = useRef(null);
  const callRef = useRef(null);
  const cueRef = useRef(null);
  const barRef = useRef(null);
  const ringRef = useRef(null);
  useHeroTilt(stageRef, ringRef);
  const wantTime = useRef(0);
  const revealed = useRef(false);
  const kickRef = useRef(() => {});
  const sound = useSound();
  const soundUpdate = sound.update;

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    let raf = 0;
    let running = false;
    let shown = null;

    // The film ends where the "Color tour" anchor lands (the tour's scroll-margin-top below the header).
    const target = () => {
      const wrap = wrapRef.current;
      const stage = stageRef.current;
      if (!wrap || !stage) return 0;
      const tour = document.getElementById("tour");
      const pad = tour ? parseFloat(getComputedStyle(tour).scrollMarginTop) || 0 : 0;
      const range = wrap.offsetHeight - stage.offsetHeight - pad;
      return range > 0 ? clamp(-wrap.getBoundingClientRect().top / range) : 0;
    };

    const seek = () => {
      const v = videoRef.current;
      if (!v || v.readyState < 1 || !v.duration || v.seeking) return;
      if (Math.abs(v.currentTime - wantTime.current) > 0.01) v.currentTime = wantTime.current;
    };

    const apply = (p) => {
      const v = videoRef.current;
      if (v?.duration) {
        wantTime.current = p * Math.max(0, v.duration - 0.04);
        soundUpdate(wantTime.current);
      }
      seek();
      const fade = smooth(clamp((p - HERO_FADE_START) / (1 - HERO_FADE_START)));
      const stage = stageRef.current;
      if (stage) {
        stage.style.opacity = String(1 - fade);
        stage.style.visibility = fade >= 0.999 ? "hidden" : "visible";
        stage.style.pointerEvents = fade < 0.5 ? "auto" : "none";
      }
      if (sceneRef.current && !reduce) sceneRef.current.style.transform = `scale(${1 + fade * 0.06})`;
      const intro = introRef.current;
      if (intro) {
        const o = 1 - smooth(clamp((p - INTRO_HOLD) / INTRO_LEN));
        intro.style.opacity = String(o);
        intro.style.transform = `translateY(${-(1 - o) * 20}px)`;
        intro.style.visibility = o > 0.01 ? "visible" : "hidden";
      }
      const mark = markRef.current;
      if (mark) {
        const m = fadeIn(mark, p, MARK_START, MARK_LEN, 14);
        mark.style.clipPath = `inset(-10% ${(1 - m) * 100}% -10% 0)`;
      }
      fadeIn(lineRef.current, p, LINE_START, LINE_LEN, 10);
      const c = fadeIn(callRef.current, p, CALL_START, CALL_LEN, 10);
      if (callRef.current) callRef.current.style.visibility = c > 0.02 ? "visible" : "hidden";
      if (cueRef.current) cueRef.current.style.opacity = String(clamp(1 - p * 14));
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
      if (!revealed.current && p >= HERO_FADE_START) {
        revealed.current = true;
        onReveal();
      }
    };

    let last = 0;
    const tick = (now) => {
      const t = target();
      const dt = last ? Math.min(64, now - last) : 16;
      last = now;
      shown = reduce ? t : shown + (t - shown) * (1 - Math.exp(-dt / EASE_MS));
      if (Math.abs(t - shown) < 0.0004) shown = t;
      apply(shown);
      if (shown !== t) {
        raf = requestAnimationFrame(tick);
      } else {
        running = false;
        last = 0;
      }
    };

    const kick = () => {
      if (shown === null) {
        shown = target();
        apply(shown);
        return;
      }
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };

    kickRef.current = () => {
      seek();
      kick();
    };
    kick();
    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", kick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", kick);
      window.removeEventListener("resize", kick);
      kickRef.current = () => {};
    };
  }, [onReveal, soundUpdate, reduce]);

  return (
    <section ref={wrapRef} className="relative h-[150vh] md:h-[320vh]" aria-label="Paint pouring from a bucket" data-testid="hero-video-section">
      <div ref={stageRef} className="sticky top-0 z-30 h-screen overflow-hidden bg-beige" style={{ "--px": 0, "--py": 0, "--tilt-scale": 1 }} data-testid="hero-video-stage">
        <div className="hero-tilt absolute inset-0" data-testid="hero-tilt-layer">
        <div ref={sceneRef} className="absolute inset-0">
          <img src={media.poster} alt="" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-poster-image" />
          {src && (
            <video
              ref={videoRef}
              src={src}
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
              disableRemotePlayback
              tabIndex={-1}
              aria-hidden="true"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
              onLoadedData={() => {
                setLoaded(true);
                kickRef.current();
              }}
              onLoadedMetadata={() => kickRef.current()}
              onSeeked={() => kickRef.current()}
              data-testid="hero-video"
            />
          )}
          <WallEnding markRef={markRef} lineRef={lineRef} callRef={callRef} />
        </div>
        </div>
        <div className="hero-glare pointer-events-none absolute inset-0" aria-hidden="true" />
        <HeroIntro ref={introRef} />
        <div ref={ringRef} className="pointer-events-none absolute left-0 top-0 z-30 h-10 w-10 rounded-full border border-wildberry/70 mix-blend-multiply" style={{ opacity: 0 }} aria-hidden="true" data-testid="hero-cursor-ring" />
        <SoundToggle sound={sound} />
        <div ref={cueRef} className="pointer-events-none absolute inset-x-0 bottom-20 flex flex-col items-center gap-3 md:bottom-10" data-testid="hero-scroll-cue">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-charcoal">Scroll</span>
          <span className="relative h-10 w-px overflow-hidden bg-charcoal/20">
            <span className="hero-cue-drip absolute inset-x-0 top-0 h-1/2 bg-wildberry" />
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-charcoal/10">
          <div ref={barRef} className="h-full origin-left bg-wildberry" style={{ transform: "scaleX(0)" }} data-testid="hero-progress-bar" />
        </div>
      </div>
    </section>
  );
};

export const HeroVideo = ScrollHero;
